import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useFiinderApi } from "@/components/api/fiinder-api-context";
import { clearVisitorScopedFiinderStorage } from "@/lib/visitor-scoped-storage";
import {
  getOrGenerateAnonymousTenantUserId,
  resetAnonymousCache,
} from "@/lib/anonymous-tenant-user-id";
import { resetInitialRoomRequested } from "@/components/ui/chat/initial-room-request-guard";

declare global {
  interface Window {
    FIINDER_TENANT_USER_ID?: string;
  }
}

/**
 * Context that exposes the current visitor identifier (`tenant_user_id`)
 * to the widget subtree. Consumers (chat hooks, fetch helpers) read this
 * value to attach it to outgoing requests. Updates flow through the
 * `tenantUserIdEmitter` on `FiinderApi`; the host triggers them via the
 * global setter `window.FIINDER_SET_TENANT_USER_ID(value)`.
 */
const TenantUserIdContext = createContext<string | undefined>(undefined);

/**
 * Reads the current visitor identifier from the surrounding provider.
 * Returns `undefined` when no identity is set (legacy fallback).
 */
export function useTenantUserId(): string | undefined {
  return useContext(TenantUserIdContext);
}

/**
 * Reads the bootstrap value of `window.FIINDER_TENANT_USER_ID`. When
 * the host injected a truthy identity, that value is used; otherwise
 * a stable anonymous identifier is provisioned from `localStorage`
 * (or generated on first call), so visitors without a host-injected
 * id still carry a consistent `tenant_user_id` across requests.
 *
 * Returns `undefined` only in SSR environments.
 */
function readInitial(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const fromHost = window.FIINDER_TENANT_USER_ID;
  if (fromHost) return fromHost;
  return getOrGenerateAnonymousTenantUserId();
}

/**
 * Provider that owns the visitor identifier state.
 *
 * It bootstraps from `window.FIINDER_TENANT_USER_ID` (read once) and
 * subscribes to `api.tenantUserIdEmitter` for runtime mutations
 * dispatched via `window.FIINDER_SET_TENANT_USER_ID(...)`. On a runtime
 * identity change (Trilho I — RF-022, CS-011), the handler purges both
 * the in-memory and the persisted TanStack caches via the dedicated
 * APIs (`queryClient.clear()` and `clearVisitorScopedFiinderStorage()`),
 * updates the `fiinder_active_visitor` marker, and only then calls
 * `setTenantUserId(value)`. The cleanup runs BEFORE the state update so
 * the next render commit already sees an empty cache — no consumer can
 * render with the previous visitor's data and the new tenantUserId
 * simultaneously. The initial mount (bootstrap path) intentionally
 * skips cleanup so pre-existing caches hydrated by the
 * `PersistQueryClient` survive when the provider is mounted lazily.
 */
export function TenantUserIdProvider({ children }: { children: ReactNode }) {
  const [tenantUserId, setTenantUserId] = useState<string | undefined>(
    readInitial,
  );
  const queryClient = useQueryClient();
  const api = useFiinderApi();

  useEffect(() => {
    function handler(value: string | undefined) {
      // Runtime change path. Cleanup MUST happen BEFORE setTenantUserId
      // so the next render commit already sees an empty cache — no
      // useQuery consumer can render with the previous visitor's data
      // and the new tenantUserId simultaneously. Bootstrap goes through
      // useState(readInitial) — never through this handler — so caches
      // hydrated by PersistQueryClient on first mount survive.
      queryClient.clear();
      clearVisitorScopedFiinderStorage();
      // The chat layer keeps a module-level guard so `requestNewRoomId`
      // runs exactly once per page load. After a tenant change the
      // subtree is remounted (see the `key=` Fragment below), so we
      // must reset that guard here — otherwise the next mount skips
      // the initial room request and the widget stays stuck with
      // `roomId === undefined`, leaving the textbox/Send disabled
      // until a full reload.
      resetInitialRoomRequested();
      window.localStorage.setItem("fiinder_active_visitor", value ?? "");
      if (value) {
        setTenantUserId(value);
      } else {
        // Host cleared the identity. Provision a fresh anonymous id so
        // the hook keeps returning a string in browser environments —
        // downstream consumers (chat, search) never observe an
        // undefined tenant_user_id mid-session.
        resetAnonymousCache();
        setTenantUserId(getOrGenerateAnonymousTenantUserId());
      }
    }
    api.tenantUserIdEmitter.on("change", handler);
    return () => api.tenantUserIdEmitter.off("change", handler);
  }, [api.tenantUserIdEmitter, queryClient]);

  // Force a full remount of the subtree whenever `tenantUserId` changes.
  // The TanStack cache cleanup (queryClient.clear) and the localStorage
  // sweep handle *persisted* state; this key-based remount handles
  // *in-memory* state that we cannot invalidate granularly — `useState`
  // locals, `useRef` values (e.g. the `roomIdRef` in `widget.tsx`),
  // ongoing animations and any consumer with state derived from the
  // previous identity. After the remount, every component reads the
  // new identity from a clean slate.
  return (
    <TenantUserIdContext.Provider value={tenantUserId}>
      <Fragment key={tenantUserId ?? "__no_visitor__"}>{children}</Fragment>
    </TenantUserIdContext.Provider>
  );
}
